import { useState, type ReactNode } from "react";
import { useEmOutraOrganizacao } from "@/components/organization/painel/OrganizacaoEmFoco";
import { AlertTriangle, Download, Loader2, PenLine, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { useConfirm } from "@/lib/confirm";
import { usePermissao } from "@/lib/use-permissao";
import { formatBRL, formatDate } from "./prosthesis-meta";
import { DENTIST_STATUS_META, dentistStatusBucket } from "./dentist-status";
import { RequestReworkDialog, type ReworkTarget } from "./RequestReworkDialog";
import { ProsthesisTeethDisplay } from "./ProsthesisTeethDisplay";
import { SentChannelsField } from "./SentChannelsField";
import type { ProsthesisOrderRow, ProsthesisServiceRow } from "./types";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";
import { LinkDoPaciente } from "./LinkDoPaciente";
import { AlteracoesDoServico, type Alteracao } from "./AlteracoesDoServico";
import { totalParaExibir, valorDoServicoEmTexto } from "@/components/prosthesis/valor-do-pedido";
import { AcrescimosDoPedido } from "./AcrescimosDoPedido";

async function downloadAttachment(utils: ReturnType<typeof trpc.useUtils>, fileId: string) {
  try {
    const { url } = await utils.file.getDownloadUrl.fetch({ fileId });
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    toast.error("Não foi possível baixar o arquivo.");
  }
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">{label}</p>
      <p className="text-sm text-zinc-900 dark:text-foreground">{value}</p>
    </div>
  );
}

function IdField({ label, id, className }: { label: string; id: string; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-400 dark:text-muted-foreground/70">{label}</p>
      <p className="select-all break-all font-mono text-[10px] text-zinc-400 dark:text-muted-foreground/70">{id}</p>
    </div>
  );
}

export function ProsthesisOrderDetailDialog({
  orderId,
  onClose,
  readOnly = false,
  onEditar,
  aviso,
}: {
  orderId: string | null;
  onClose: () => void;
  /** Resumo do Início — só mostra o pedido, sem "Enviado também por" editável nem "Solicitar
   *  retrabalho". A edição completa continua em /protese e /agenda (default). */
  readOnly?: boolean;
  /** Quem hospeda o formulário de pedido oferece a edição por aqui; sem isso o botão não aparece
   *  (o diálogo sozinho não tem onde abrir o formulário). */
  onEditar?: (order: ProsthesisOrderRow) => void;
  /** Faixa logo abaixo do título — o "pedido de outra organização, só leitura" do acesso pessoal. */
  aviso?: ReactNode;
}) {
  const [reworkTarget, setReworkTarget] = useState<ReworkTarget>(null);
  // Em foco de outra organização o link da ficha abriria a ativa — o nome fica só texto.
  const emOutra = useEmOutraOrganizacao();
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const podeGerenciar = usePermissao("prosthesis.manage");
  const orderQuery = trpc.prosthesis.getById.useQuery({ id: orderId ?? "" }, { enabled: !!orderId });
  const order = orderQuery.data as ProsthesisOrderRow | undefined;
  const alteracoesQuery = trpc.prosthesis.changes.listByOrder.useQuery({ orderId: orderId ?? "" }, { enabled: !!orderId });
  const alteracoes = (alteracoesQuery.data ?? []) as Alteracao[];

  const remover = trpc.prosthesis.remove.useMutation({
    onSuccess: () => {
      toast.success("Pedido excluído.");
      utils.prosthesis.list.invalidate();
      utils.prosthesis.countByStatus.invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.message || "Não foi possível excluir o pedido."),
  });

  async function excluir() {
    if (!order) return;
    const ok = await confirm({
      title: "Excluir este pedido?",
      description:
        order.status === "AGUARDANDO_CONFIRMACAO"
          ? "O laboratório ainda não confirmou — o pedido some da fila dele e nada fica registrado."
          : "O pedido será removido da sua lista. Essa ação não pode ser desfeita.",
      confirmText: "Excluir pedido",
      cancelText: "Cancelar",
      destructive: true,
    });
    if (ok) remover.mutate({ id: order.id });
  }

  // A mesma regra do servidor: editar só antes do aceite; excluir antes do aceite ou depois que o
  // laboratório desistiu. Entre o aceite e a entrega, o pedido é trabalho na bancada de outra
  // organização — a clínica pede retrabalho, não reescreve.
  const podeEditar = !readOnly && podeGerenciar && !!onEditar && order?.status === "AGUARDANDO_CONFIRMACAO";
  const podeExcluir =
    !readOnly && podeGerenciar && !!order && ["AGUARDANDO_CONFIRMACAO", "RECUSADO", "CANCELADO"].includes(order.status);

  return (
    <>
      <Dialog open={!!orderId} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto dark:bg-card">
          {orderQuery.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : order ? (
            <>
              <DialogHeader>
                <DialogTitle ajuda={AJUDA_PROTESE.detalheDoPedido} classeDoGrupo="pr-8">
                  {emOutra ? order.patientName : <LinkDoPaciente patientId={order.patientId} patientName={order.patientName} onAbrir={onClose} />}
                  {order.numero ? (
                    <span className="ml-2 align-middle font-mono text-xs font-semibold text-zinc-400 dark:text-muted-foreground">
                      Pedido nº {order.numero}
                    </span>
                  ) : null}
                  {order.itemDoPlano ? (
                    <span className="ml-2 align-middle text-xs font-medium text-brand-700 dark:text-primary">
                      Do plano de tratamento nº {order.itemDoPlano.plano.numero}
                    </span>
                  ) : null}
                </DialogTitle>
              </DialogHeader>
              {aviso}

              {/* Três colunas iguais: Laboratório e Dentista ficam sobre Data e Prazo. */}
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-zinc-50 p-3 text-sm dark:bg-background/50 sm:grid-cols-3">
                <Field label="Laboratório" value={order.labName ?? "Sem laboratório definido"} className="col-span-2 sm:col-span-1" />
                <Field label="Dentista" value={order.dentistName ?? "—"} className="col-span-2 sm:col-span-1" />
                <Field label="Data do pedido" value={formatDate(order.createdAt)} className="sm:col-start-1" />
                <Field label="Prazo" value={formatDate(order.deadline)} />
                <Field
                  className="col-span-2 sm:col-span-1"
                  label="Valor total"
                  value={formatBRL(totalParaExibir(order.services, order.acrescimos))}
                />
                <IdField label="ID do pedido" id={order.id} className="col-span-2 sm:col-span-3" />
              </div>

              {order.status === "RECUSADO" && order.declineReason && (
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 dark:border-red-900/40 dark:bg-red-950/10">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
                    Motivo da recusa
                  </p>
                  <p className="text-sm text-zinc-700 dark:text-foreground/80">{order.declineReason}</p>
                </div>
              )}

              {order.description && (
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                    Observações gerais
                  </p>
                  <p className="text-sm text-zinc-700 dark:text-foreground/80">{order.description}</p>
                </div>
              )}

              <SentChannelsField orderId={order.id} sentChannels={order.sentChannels} readOnly={readOnly} />

              {(podeEditar || podeExcluir) && (
                <div className="flex flex-wrap items-center gap-2">
                  {podeEditar && (
                    <button
                      type="button"
                      onClick={() => onEditar!(order)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-500/10"
                    >
                      <PenLine className="h-3.5 w-3.5" />
                      Editar pedido
                    </button>
                  )}
                  {podeExcluir && (
                    <button
                      type="button"
                      disabled={remover.isPending}
                      onClick={excluir}
                      className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-950/20"
                    >
                      {remover.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      Excluir pedido
                    </button>
                  )}
                  {podeEditar && (
                    <span className="text-[11.5px] text-zinc-500 dark:text-muted-foreground">
                      Disponível até o laboratório confirmar.
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
                  Serviços ({order.services.length})
                </p>
                <div className="flex flex-col gap-2">
                  {order.services.map((service) => (
                    <ServiceRow
                      key={service.id}
                      service={service}
                      alteracoes={alteracoes.filter((a) => a.serviceId === service.id)}
                      readOnly={readOnly}
                      onRequestRework={() =>
                        setReworkTarget({
                          serviceId: service.id,
                          serviceLabel: service.serviceCategory.label,
                          patientName: order.patientName,
                        })
                      }
                    />
                  ))}
                </div>
              </div>

              <AcrescimosDoPedido orderId={order.id} acrescimos={order.acrescimos ?? []} podeEditar={false} />
            </>
          ) : (
            <p className="py-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">Pedido não encontrado.</p>
          )}
        </DialogContent>
      </Dialog>

      <RequestReworkDialog target={reworkTarget} onClose={() => setReworkTarget(null)} />
    </>
  );
}

/** Mesmo layout do serviço usado em /lab/producao (ProductionOrderDetailDialog) — odontograma
 *  ao lado dos campos — só que aqui o status é só leitura (quem avança a produção é o
 *  laboratório) e tem o botão de solicitar retrabalho no lugar do download de anexo. */
function ServiceRow({
  service,
  alteracoes,
  onRequestRework,
  readOnly = false,
}: {
  service: ProsthesisServiceRow;
  alteracoes: Alteracao[];
  onRequestRework: () => void;
  readOnly?: boolean;
}) {
  const utils = trpc.useUtils();
  // Dentista vê só o status simplificado (mesmo bucket usado na lista/cards de Prótese) — os 15
  // status granulares do laboratório (Vazar Gesso, Fresando/Imprimindo...) não fazem sentido aqui.
  const statusMeta = DENTIST_STATUS_META[dentistStatusBucket(service.status)];
  // Só faz sentido pedir retrabalho depois que o laboratório entregou — é aí que o dentista
  // consegue avaliar se o serviço saiu certo ou precisa ser refeito. Checagem contra o status
  // granular real (não o bucket), continua igual. Nunca no modo readOnly (resumo do Início).
  const canRequestRework = !readOnly && service.status === "CONCLUIDO";

  return (
    <div className="rounded-lg border border-zinc-200 p-3 dark:border-border">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
          {service.serviceCategory.label}
        </p>
        <span
          className="inline-flex shrink-0 items-center gap-1 justify-self-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase"
          style={{ backgroundColor: statusMeta.bg, color: statusMeta.color }}
        >
          {service.hasOpenRework && <AlertTriangle className="h-2.5 w-2.5" />}
          {statusMeta.label}
        </span>
        {service.priceCents != null && (
          <span className="shrink-0 justify-self-end text-sm font-medium tabular-nums text-zinc-700 dark:text-foreground/80">
            {valorDoServicoEmTexto(service)}
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-col gap-6 sm:flex-row">
        <div className="w-full shrink-0 sm:w-[130px]">
          <ProsthesisTeethDisplay teeth={service.teeth} />
        </div>
        <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-2">
          <Field label="Serviço" value={service.serviceCategory.label} />
          <Field label="Dentes" value={service.teeth || "—"} />
          <Field label="Material" value={rotuloDoMaterialDoServico(service) ?? "—"} />
          <Field label="Cor" value={service.shadeOption?.code ?? "—"} />
          <Field label="Observações" value={service.notes || "—"} className="col-span-2" />
          <IdField label="ID do serviço" id={service.id} className="col-span-2" />
        </div>
      </div>

      <AlteracoesDoServico alteracoes={alteracoes} />

      {(service.attachmentFile || canRequestRework) && (
        <div className="mt-3 flex items-center justify-between gap-2">
          <div>
            {service.attachmentFile && (
              <button
                type="button"
                onClick={() => downloadAttachment(utils, service.attachmentFile!.id)}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-[12px] font-medium text-brand-700 transition-colors hover:bg-brand-50 dark:border-border dark:text-primary dark:hover:bg-primary/10"
              >
                <Download className="h-3.5 w-3.5" />
                Baixar arquivo anexado
              </button>
            )}
          </div>
          <div>
            {canRequestRework && (
              <button
                type="button"
                onClick={onRequestRework}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-red-200 px-2.5 text-[12px] font-semibold text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Solicitar Retrabalho
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
